"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlayerAvatar } from "@/components/common/player-avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  Trophy, 
  Clock, 
  MapPin, 
  Target, 
  TrendingUp, 
  TrendingDown, 
  Users,
  Flag,
  DollarSign,
  Calendar,
  CheckCircle,
  X,
  AlertTriangle,
} from "lucide-react";
import { InlineLoading } from "@/components/ui/loading";
import { cn } from "@/lib/utils";
import type { MatchFormat, TimePreference, MatchInvitation, OpponentProfile, MatchDetails } from "../types";

// Using types from ../types

interface MatchInvitationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invitation: MatchInvitation;
  opponent: OpponentProfile;
  onAccept: () => void;
  onDecline: (reason?: string) => void;
  isProcessing?: boolean;
  mode?: "received" | "accepted";
  userMmr?: number;
}

export function MatchInvitationDialog({
  isOpen,
  onClose,
  invitation,
  opponent,
  onAccept,
  onDecline,
  isProcessing = false,
  mode = "received",
  userMmr = 1200,
}: MatchInvitationDialogProps) {
  const matchDetails = invitation.matchDetails;

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'expert': return 'text-purple-600 bg-purple-50';
      case 'advanced': return 'text-blue-600 bg-blue-50';
      case 'intermediate': return 'text-green-600 bg-green-50';
      default: return 'text-orange-600 bg-orange-50';
    }
  };

  const calculateMmrChange = () => {
    const mmrDiff = Math.abs(userMmr - opponent.mmr);
    const baseMmrChange = 25;
    
    // Adjust based on MMR difference
    let mmrChange = baseMmrChange;
    if (mmrDiff > 200) {
      mmrChange = userMmr > opponent.mmr ? 15 : 35; // Less gain vs higher, more vs lower
    } else if (mmrDiff > 100) {
      mmrChange = userMmr > opponent.mmr ? 20 : 30;
    }

    return {
      win: mmrChange,
      loss: Math.floor(mmrChange * 0.8), // Slightly less loss than potential gain
    };
  };

  const mmrChanges = calculateMmrChange();

  const getTimeDisplayText = () => {
    const { timePreference } = matchDetails;
    
    switch (timePreference.type) {
      case "now":
        return "Playing now (ready immediately)";
      case "soon":
        return "Within 30 minutes";
      case "tee_time":
        if (timePreference.teeTime) {
          const date = new Date(timePreference.teeTime.date).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          });
          const time = new Date(`2000-01-01T${timePreference.teeTime.time}`).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          return `${date} at ${time}${timePreference.teeTime.flexible ? ' (flexible)' : ''}`;
        }
        return "Scheduled tee time";
      default:
        return "Time TBD";
    }
  };

  const getDialogTitle = () => {
    switch (mode) {
      case "received":
        return "Match Challenge Received";
      case "accepted":
        return "Match Challenge Accepted";
      default:
        return "Match Invitation";
    }
  };

  const getDialogDescription = () => {
    switch (mode) {
      case "received":
        return "You've been challenged to a golf match! Review the details below.";
      case "accepted":
        return "Someone accepted your challenge! Confirm to finalize the match.";
      default:
        return "Review the match invitation details.";
    }
  };

  const getAcceptButtonText = () => {
    if (isProcessing) {
      return mode === "received" ? "Accepting..." : "Confirming...";
    }
    return mode === "received" ? "Accept Challenge" : "Confirm Match";
  };

  const getStakeIcon = () => {
    switch (matchDetails.matchFormat.stakes.type) {
      case "money_wager":
        return DollarSign;
      case "mmr_only":
        return Trophy;
      default:
        return Target;
    }
  };

  const StakeIcon = getStakeIcon();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Opponent Profile */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4 mb-4">
                <PlayerAvatar 
                  name={opponent.displayName} 
                  src={opponent.avatar} 
                  className="h-16 w-16"
                />
                
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">{opponent.displayName}</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={cn("text-xs", getTierColor(opponent.skillTier))}>
                      {opponent.skillTier}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      MMR {opponent.mmr}
                    </Badge>
                    <Badge variant="outline" className="text-xs text-green-600">
                      {opponent.compatibilityScore}% match
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{opponent.matchesPlayed} matches</span>
                    <span>{opponent.winRate}% win rate</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {opponent.distance} mi away
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-2 gap-3 text-center">
                {opponent.averageScore && (
                  <div className="bg-muted/50 rounded-lg p-2">
                    <div className="font-semibold">{opponent.averageScore}</div>
                    <div className="text-xs text-muted-foreground">Avg Score</div>
                  </div>
                )}
                {opponent.handicap && (
                  <div className="bg-muted/50 rounded-lg p-2">
                    <div className="font-semibold">{opponent.handicap}</div>
                    <div className="text-xs text-muted-foreground">Handicap</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Match Details */}
          <Card>
            <CardContent className="pt-4 space-y-4">
              <h4 className="font-semibold flex items-center gap-2">
                <Flag className="h-4 w-4" />
                Match Details
              </h4>
              
              <div className="space-y-3">
                {/* Course */}
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{matchDetails.courseName}</div>
                    {matchDetails.courseAddress && (
                      <div className="text-sm text-muted-foreground">
                        {matchDetails.courseAddress}
                      </div>
                    )}
                  </div>
                </div>

                {/* Time */}
                <div className="flex items-start gap-3">
                  <Clock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{getTimeDisplayText()}</div>
                    <div className="text-sm text-muted-foreground">
                      Est. duration: {matchDetails.estimatedDuration}
                    </div>
                  </div>
                </div>

                {/* Format */}
                <div className="flex items-start gap-3">
                  <Users className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <div className="font-medium">
                      {matchDetails.matchFormat.holes} holes - {matchDetails.matchFormat.format.replace('_', ' ')}
                    </div>
                    <div className="text-sm text-muted-foreground capitalize">
                      {matchDetails.matchFormat.competitiveLevel.replace('_', ' ')} level
                    </div>
                  </div>
                </div>

                {/* Stakes */}
                <div className="flex items-start gap-3">
                  <StakeIcon className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <div className="font-medium">Stakes</div>
                    <div className="text-sm text-muted-foreground">
                      {matchDetails.matchFormat.stakes.description}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* MMR Impact */}
          {matchDetails.matchFormat.stakes.type !== "friendly" && (
            <Card>
              <CardContent className="pt-4">
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <Trophy className="h-4 w-4" />
                  MMR Impact
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg border">
                    <TrendingUp className="h-5 w-5 text-green-600 mx-auto mb-1" />
                    <div className="font-semibold text-green-600">+{mmrChanges.win}</div>
                    <div className="text-xs text-muted-foreground">If you win</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg border">
                    <TrendingDown className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <div className="font-semibold text-red-600">-{mmrChanges.loss}</div>
                    <div className="text-xs text-muted-foreground">If you lose</div>
                  </div>
                </div>

                <div className="mt-3 text-center text-xs text-muted-foreground">
                  MMR changes based on skill difference and match importance
                </div>
              </CardContent>
            </Card>
          )}

          {/* Money stakes warning */}
          {matchDetails.matchFormat.stakes.type === "money_wager" && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-amber-800">Money Match</div>
                <div className="text-amber-700">
                  Both players agree to ${matchDetails.matchFormat.stakes.amount} wager. 
                  Winner takes ${(matchDetails.matchFormat.stakes.amount || 0) * 2}. 
                  Course fees are separate.
                </div>
              </div>
            </div>
          )}

          {/* Next steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-sm">
              <div className="font-medium text-blue-800 mb-1">Next Steps:</div>
              <div className="text-blue-700 space-y-1">
                <div>• Accept this invitation to confirm the match</div>
                <div>• Coordinate tee time booking with your opponent</div>
                <div>• Meet at the course at the agreed time</div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onDecline()}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <InlineLoading text="Processing..." />
            ) : (
              "Decline"
            )}
          </Button>
          <Button
            onClick={onAccept}
            disabled={isProcessing}
            className="bg-green-600 hover:bg-green-700"
          >
            {isProcessing ? (
              <InlineLoading text={getAcceptButtonText()} />
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                {getAcceptButtonText()}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
